import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Alert, AlertDescription } from './alert';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './input-otp';
import { ArrowLeft, Smartphone, Mail, Clock, ShieldAlert } from 'lucide-react';
import { toast } from './sonner';
import {
  OTP_SECURITY,
  applyServerLockout,
  clearOtpVerifyGuard,
  formatCountdown,
  formatLockoutDuration,
  getOtpVerifyGuardState,
  getResendCooldownRemaining,
  isValidOtpIdentifier,
  markOtpSent,
  maskOtpIdentifier,
  normalizeOtpIdentifier,
  recordOtpVerifyFailure,
  type OtpChannel,
} from '@vaiu/utils';

export interface OtpSendResponse {
  message?: string;
  expiresIn?: number;
  expiresAt?: string;
}

export interface OtpStatusResponse {
  hasActiveCode?: boolean;
  canResend?: boolean;
  expiresAt?: string | null;
  verifyLockedUntil?: string | null;
  verifyAttemptsRemaining?: number;
}

export interface OtpLoginApi {
  requestOtp: (identifier: string, type: OtpChannel) => Promise<OtpSendResponse>;
  verifyOtp: (
    identifier: string,
    code: string,
    type: OtpChannel
  ) => Promise<{ token: string; username: string }>;
  getOtpStatus?: (type: OtpChannel, identifier: string) => Promise<OtpStatusResponse>;
}

export interface OtpLoginLabels {
  title: string;
  chooseMethodTitle: string;
  chooseMethodDescription: string;
  sms: string;
  email: string;
  continueWith: (method: string) => string;
  enterPhoneTitle: string;
  enterEmailTitle: string;
  enterPhoneDescription: string;
  enterEmailDescription: string;
  phoneLabel: string;
  emailLabel: string;
  phonePlaceholder: string;
  emailPlaceholder: string;
  phoneHint: string;
  invalidPhone: string;
  invalidEmail: string;
  sendCode: string;
  sending: string;
  back: string;
  verifyTitle: string;
  verifyDescription: (masked: string) => string;
  codeExpiresIn: (time: string) => string;
  verifying: string;
  verify: string;
  resendPrompt: string;
  resendWait: (time: string) => string;
  otpSentTitle: string;
  otpSentDescription: (channel: string) => string;
  loginSuccessTitle: string;
  loginSuccessDescription: string;
  resendCooldown: string;
  verifyLocked: (time: string) => string;
  attemptsRemaining: (count: number) => string;
  enterCompleteCode: string;
  sendFailed: string;
  verifyFailed: string;
  accountNotFound: string;
  tooManyAttempts: string;
  resendBlocked: string;
}

export interface OtpLoginProps {
  onSuccess: (token: string) => void;
  onBack: () => void;
  api: OtpLoginApi;
  labels: OtpLoginLabels;
}

function parseApiError(err: unknown): { message: string; status?: number } {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as Error).message);
    const status = 'status' in err ? Number((err as { status?: number }).status) : undefined;
    return { message, status };
  }
  return { message: 'Request failed' };
}

const OtpLogin: React.FC<OtpLoginProps> = ({ onSuccess, onBack, api, labels }) => {
  const [step, setStep] = useState<'method' | 'identifier' | 'verify'>('method');
  const [method, setMethod] = useState<OtpChannel>('sms');
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expirySeconds, setExpirySeconds] = useState(0);
  const [resendCooldownMs, setResendCooldownMs] = useState(0);
  const [verifyGuard, setVerifyGuard] = useState(() =>
    getOtpVerifyGuardState('sms', '')
  );
  const [tick, setTick] = useState(0);

  const normalizedIdentifier = useMemo(
    () => (identifier ? normalizeOtpIdentifier(method, identifier) : ''),
    [identifier, method]
  );

  useEffect(() => {
    const timer = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!normalizedIdentifier) return;
    setVerifyGuard(getOtpVerifyGuardState(method, normalizedIdentifier));
    setResendCooldownMs(getResendCooldownRemaining(method, normalizedIdentifier));
  }, [method, normalizedIdentifier, tick]);

  useEffect(() => {
    if (expirySeconds <= 0) return;
    const timer = window.setTimeout(() => setExpirySeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [expirySeconds]);

  const canResend = resendCooldownMs <= 0 && !verifyGuard.isLocked;

  const syncStatusFromApi = useCallback(async () => {
    if (!api.getOtpStatus || !normalizedIdentifier) return;
    try {
      const status = await api.getOtpStatus(method, normalizedIdentifier);
      if (status.verifyLockedUntil) {
        setVerifyGuard(applyServerLockout(method, normalizedIdentifier, status.verifyLockedUntil));
      }
      if (status.expiresAt) {
        const remaining = Math.max(
          0,
          Math.floor((new Date(status.expiresAt).getTime() - Date.now()) / 1000)
        );
        if (remaining > 0) setExpirySeconds(remaining);
      }
      if (status.canResend === false) {
        const localRemaining = getResendCooldownRemaining(method, normalizedIdentifier);
        if (localRemaining <= 0) {
          markOtpSent(method, normalizedIdentifier);
        }
      }
    } catch {
      // Status is optional; ignore failures
    }
  }, [api, method, normalizedIdentifier]);

  useEffect(() => {
    if (step === 'verify') {
      void syncStatusFromApi();
    }
  }, [step, syncStatusFromApi]);

  const handleSendOtp = async () => {
    const normalized = normalizeOtpIdentifier(method, identifier);
    if (!isValidOtpIdentifier(method, normalized)) {
      setError(method === 'email' ? labels.invalidEmail : labels.invalidPhone);
      return;
    }

    const cooldown = getResendCooldownRemaining(method, normalized);
    if (cooldown > 0) {
      setError(labels.resendWait(formatLockoutDuration(cooldown)));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await api.requestOtp(normalized, method);
      markOtpSent(method, normalized);
      setResendCooldownMs(OTP_SECURITY.RESEND_COOLDOWN_MS);
      setExpirySeconds(result.expiresIn ?? OTP_SECURITY.OTP_EXPIRY_SEC);
      setStep('verify');
      setOtp('');
      toast.success(labels.otpSentTitle, {
        description: labels.otpSentDescription(method === 'sms' ? labels.sms : labels.email),
      });
    } catch (err) {
      const { message, status } = parseApiError(err);
      if (status === 404 || message.toLowerCase().includes('no account')) {
        setError(labels.accountNotFound);
      } else if (status === 429 || message.toLowerCase().includes('wait')) {
        setError(labels.resendBlocked);
        markOtpSent(method, normalized);
      } else {
        setError(message || labels.sendFailed);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const normalized = normalizeOtpIdentifier(method, identifier);
    const guard = getOtpVerifyGuardState(method, normalized);

    if (guard.isLocked && guard.lockoutEndsAt) {
      setVerifyGuard(guard);
      setError(labels.verifyLocked(formatLockoutDuration(guard.lockoutEndsAt - Date.now())));
      return;
    }

    if (otp.length !== 6) {
      setError(labels.enterCompleteCode);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await api.verifyOtp(normalized, otp, method);
      clearOtpVerifyGuard(method, normalized);
      toast.success(labels.loginSuccessTitle, { description: labels.loginSuccessDescription });
      onSuccess(result.token);
    } catch (err) {
      const { message, status } = parseApiError(err);

      if (status === 429 || message.toLowerCase().includes('too many')) {
        const locked = applyServerLockout(method, normalized, Date.now() + OTP_SECURITY.VERIFY_LOCKOUT_MS);
        setVerifyGuard(locked);
        setError(labels.tooManyAttempts);
      } else if (status === 401 || message.toLowerCase().includes('invalid')) {
        const next = recordOtpVerifyFailure(method, normalized);
        setVerifyGuard(next);
        if (next.isLocked && next.lockoutEndsAt) {
          setError(labels.verifyLocked(formatLockoutDuration(next.lockoutEndsAt - Date.now())));
        } else {
          setError(
            `${labels.verifyFailed} ${labels.attemptsRemaining(next.attemptsRemaining)}`
          );
        }
      } else if (status === 404) {
        setError(labels.accountNotFound);
      } else {
        setError(message || labels.verifyFailed);
      }
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (!canResend || verifyGuard.isLocked) return;
    setOtp('');
    void handleSendOtp();
  };

  const renderMethodSelection = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">{labels.chooseMethodTitle}</h3>
        <p className="text-sm text-muted-foreground">{labels.chooseMethodDescription}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Button
          variant={method === 'sms' ? 'default' : 'outline'}
          className="h-20 flex-col space-y-2"
          onClick={() => setMethod('sms')}
          type="button"
        >
          <Smartphone className="w-6 h-6" />
          <span>{labels.sms}</span>
        </Button>
        <Button
          variant={method === 'email' ? 'default' : 'outline'}
          className="h-20 flex-col space-y-2"
          onClick={() => setMethod('email')}
          type="button"
        >
          <Mail className="w-6 h-6" />
          <span>{labels.email}</span>
        </Button>
      </div>

      <Button onClick={() => setStep('identifier')} className="w-full" type="button">
        {labels.continueWith(method === 'sms' ? labels.sms : labels.email)}
      </Button>
    </div>
  );

  const renderIdentifierInput = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">
          {method === 'sms' ? labels.enterPhoneTitle : labels.enterEmailTitle}
        </h3>
        <p className="text-sm text-muted-foreground">
          {method === 'sms' ? labels.enterPhoneDescription : labels.enterEmailDescription}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="otp-identifier">
          {method === 'sms' ? labels.phoneLabel : labels.emailLabel}
        </Label>
        <Input
          id="otp-identifier"
          type={method === 'sms' ? 'tel' : 'email'}
          autoComplete={method === 'sms' ? 'tel' : 'email'}
          placeholder={method === 'sms' ? labels.phonePlaceholder : labels.emailPlaceholder}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          className={error ? 'border-red-500' : ''}
        />
        {method === 'sms' && (
          <p className="text-xs text-muted-foreground">{labels.phoneHint}</p>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex space-x-2">
        <Button variant="outline" onClick={() => setStep('method')} className="flex-1" type="button">
          {labels.back}
        </Button>
        <Button
          onClick={() => void handleSendOtp()}
          disabled={loading || !identifier.trim()}
          className="flex-1"
          type="button"
        >
          {loading ? labels.sending : labels.sendCode}
        </Button>
      </div>
    </div>
  );

  const renderOtpVerification = () => {
    const masked = maskOtpIdentifier(method, identifier);
    const lockoutRemaining =
      verifyGuard.isLocked && verifyGuard.lockoutEndsAt
        ? verifyGuard.lockoutEndsAt - Date.now()
        : 0;

    return (
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">{labels.verifyTitle}</h3>
          <p className="text-sm text-muted-foreground">{labels.verifyDescription(masked)}</p>
        </div>

        {verifyGuard.isLocked && lockoutRemaining > 0 && (
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>
              {labels.verifyLocked(formatLockoutDuration(lockoutRemaining))}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={otp}
              onChange={setOtp}
              disabled={loading || verifyGuard.isLocked}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          {expirySeconds > 0 && (
            <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span>{labels.codeExpiresIn(formatCountdown(expirySeconds))}</span>
            </div>
          )}

          {!verifyGuard.isLocked && verifyGuard.attemptsRemaining < OTP_SECURITY.MAX_VERIFY_ATTEMPTS && (
            <p className="text-center text-xs text-muted-foreground">
              {labels.attemptsRemaining(verifyGuard.attemptsRemaining)}
            </p>
          )}

          {error && !verifyGuard.isLocked && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setStep('identifier')}
              className="flex-1"
              type="button"
            >
              {labels.back}
            </Button>
            <Button
              onClick={() => void handleVerifyOtp()}
              disabled={loading || otp.length !== 6 || verifyGuard.isLocked}
              className="flex-1"
              type="button"
            >
              {loading ? labels.verifying : labels.verify}
            </Button>
          </div>

          <div className="text-center">
            {resendCooldownMs > 0 ? (
              <p className="text-sm text-muted-foreground">
                {labels.resendWait(formatLockoutDuration(resendCooldownMs))}
              </p>
            ) : canResend && !verifyGuard.isLocked ? (
              <Button variant="link" onClick={handleResend} className="text-sm" type="button">
                {labels.resendPrompt}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={onBack} type="button">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <CardTitle>{labels.title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {step === 'method' && renderMethodSelection()}
        {step === 'identifier' && renderIdentifierInput()}
        {step === 'verify' && renderOtpVerification()}
      </CardContent>
    </Card>
  );
};

export default OtpLogin;
